import type { FlexComponent } from "@/utils/render-component";

import { computed, defineComponent, nextTick, onUnmounted, ref, watch } from "vue";
import { renderComponent } from "@/utils/render-component";
import Sheet from "../primitives/sheet/Sheet";

declare const _SIDE_OPTIONS: readonly ["top", "right", "bottom", "left"];
declare const _ALIGN_OPTIONS: readonly ["start", "center", "end"];
export type Side = (typeof _SIDE_OPTIONS)[number];
export type Align = (typeof _ALIGN_OPTIONS)[number];
export type SheetSize = "sm" | "md" | "lg";

const activeSheet = ref<FlexComponent | null>(null);
const sheetSide = ref<Side>("right");
const sheetSize = ref<SheetSize>("sm");
let focusedElementBeforeModal: Element | null = null;

export function openSheet(component: FlexComponent, side?: Side, size?: SheetSize) {
  if (activeSheet.value) throw new Error("Modal already open");
  if (side) sheetSide.value = side;
  if (size) sheetSize.value = size;

  focusedElementBeforeModal = document.activeElement;
  activeSheet.value = component;
}

export function closeSheet() {
  activeSheet.value = null;
  sheetSize.value = "sm"; // Reset to default

  nextTick(() => {
    if (focusedElementBeforeModal) {
      (focusedElementBeforeModal as HTMLElement)?.focus();
      focusedElementBeforeModal = null;
    }
  });
}

export default defineComponent({
  name: "Modal",
  setup() {
    const open = computed({
      get: () => !!activeSheet.value,
      set: (value) => {
        if (!value) closeSheet();
      },
    });

    watch(activeSheet, (value) => {
      if (value) open.value = true;
      else open.value = false;
    });

    onUnmounted(() => {
      closeSheet();
    });

    return () => (
      <Sheet.Root v-model:open={open.value}>
        <Sheet.Content
          class="bg-card"
          side={sheetSide.value}
          size={sheetSize.value}
          onCloseAutoFocus={() => {
            open.value = false;
          }}
        >
          {activeSheet.value ? renderComponent(activeSheet.value) : null}
        </Sheet.Content>
      </Sheet.Root>
    );
  },
});
